import { useState, useEffect } from "react";

export type LayoutMode = "mobile-portrait" | "mobile-landscape" | "desktop";

/**
 * U03 — layout-mode hook. Routes every consumer onto one of three modes.
 *
 * Resolution order:
 * 1. `?layout=mobile-portrait|mobile-landscape|desktop` query-string
 *    override (dev/screenshot).
 * 2. `(pointer: fine)` + viewport width ≥ 1024 → desktop.
 * 3. Orientation decides between `mobile-portrait` and `mobile-landscape`.
 *
 * The HUD root tags its CSS grid with `data-layout-mode` so components can
 * style purely through CSS and only branch in JS where the *behavior*
 * differs (e.g. full-screen modal vs inline popover).
 */

const OVERRIDE_VALUES = new Set<LayoutMode>([
  "mobile-portrait",
  "mobile-landscape",
  "desktop",
]);

function readOverride(): LayoutMode | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("layout");
  return raw && OVERRIDE_VALUES.has(raw as LayoutMode)
    ? (raw as LayoutMode)
    : null;
}

function detect(): LayoutMode {
  const override = readOverride();
  if (override) return override;
  if (typeof window === "undefined") return "desktop";

  // jsdom (test env) has no matchMedia — treat it as a fine-pointer
  // desktop so component render tests see a stable layout.
  const finePointer =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: fine)").matches
      : true;
  const wide = window.innerWidth >= 1024;
  if (finePointer && wide) return "desktop";

  const landscape = window.innerWidth > window.innerHeight;
  return landscape ? "mobile-landscape" : "mobile-portrait";
}

export function useLayout(): LayoutMode {
  const [mode, setMode] = useState<LayoutMode>(detect);

  useEffect(() => {
    const handle = () => setMode(detect());
    window.addEventListener("resize", handle);
    window.addEventListener("orientationchange", handle);
    const fine =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(pointer: fine)")
        : null;
    fine?.addEventListener("change", handle);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("orientationchange", handle);
      fine?.removeEventListener("change", handle);
    };
  }, []);

  return mode;
}
